import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Element } from '../../../element.model';
import { DataService } from '../../data.service';
import { ElementWithdrawalService } from '../../../Services/element-withdrawal.service';
import { WithdrawalLogModel } from '../../../Models/withdrawal-log.model';

@Component({
  selector: 'app-element-dialog',
  templateUrl: './element-dialog.component.html',
  styleUrls: ['./element-dialog.component.css']
})
export class ElementDialogComponent implements OnInit {
  imageUrl: any;
  categories: string[] = [];
  warehouses: string[] = [];
  locations: string[] = [];
  isUploading: boolean = false;
  isImageLoading: boolean = false;
  iscopy: boolean = false;

  // Withdrawal related properties
  withdrawalForm: FormGroup;
  withdrawalHistory: WithdrawalLogModel[] = [];
  displayedColumns: string[] = ['timestamp', 'userName', 'quantity', 'purpose', 'comments'];
  isLoadingHistory = false;
  isWithdrawing = false;
  selectedTabIndex = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<ElementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { element: Element, iscopy?: boolean },
    private dataService: DataService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private withdrawalService: ElementWithdrawalService
  ) {
    this.loadImage();
    this.iscopy = data.iscopy || false;
    // Initialize withdrawal form
    this.withdrawalForm = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(this.data.element.quantity)]],
      purpose: ['', Validators.required],
      comments: ['']
    });
  }

  ngOnInit(): void {
    this.loadImage();
    this.loadFilterOptions();
    // Load locations for the current warehouse
    if (this.data.element.lager) {
      this.loadLocations(this.data.element.lager);
    }

    // Load withdrawal history
    this.loadWithdrawalHistory();
  }

  private loadFilterOptions(): void {
    this.dataService.getCategories().subscribe(categories => {
      this.categories = categories;
    });
    this.dataService.getWarehouses().subscribe(warehouses => {
      this.warehouses = warehouses;
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close(this.data.element);
  }

  onImageUpload(event: Event) {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (file) {
      this.isUploading = true;
      this.dataService.uploadFile(file, this.data.element.id).subscribe({
        next: (response: any) => {
          this.data.element.imagePath = response.path;
          this.loadImage();
          this.isUploading = false;
        },
        error: (err) => {
          console.error('Upload error:', err);
          this.isUploading = false;
        }
      });
    }
  }

  loadImage(): void {
    if (this.data.element.imagePath) {
      this.isImageLoading = true;
      this.dataService.getFile(this.data.element.id).subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(url);
          this.isImageLoading = false;
        },
        error: (err) => {
          console.error('Image loading error:', err);
          this.isImageLoading = false;
        }
      });
    }
  }

  onLagerChanged(): void {
    if (this.data.element.lager) {
      this.loadLocations(this.data.element.lager);
    } else {
      this.locations = [];
      this.data.element.lagerort = '';
    }
  }

  private loadLocations(lager: string): void {
    this.dataService.getLagerorte(lager).subscribe(locations => {
      this.locations = locations;
    });
  }

  openFileInput() {
    document.getElementById('fileInput')?.click();
  }

  // New methods for withdrawal functionality
  onTabChanged(event: any): void {
    this.selectedTabIndex = event.index;
    if (event.index === 1) {
      // When withdrawal history tab is selected, refresh the data
      this.loadWithdrawalHistory();
    }
  }

  loadWithdrawalHistory(): void {
    this.isLoadingHistory = true;
    this.withdrawalService.getWithdrawalHistoryByElement(this.data.element.id)
      .subscribe({
        next: (history) => {
          this.withdrawalHistory = history;
          this.isLoadingHistory = false;

          // Initialize paginator and sort after data is loaded
          if (this.paginator && this.sort) {
            setTimeout(() => {
              this.paginator.length = this.withdrawalHistory.length;
            });
          }
        },
        error: (error) => {
          console.error('Error loading withdrawal history:', error);
          this.snackBar.open('Fehler beim Laden der Entnahmehistorie', 'OK', { duration: 3000 });
          this.isLoadingHistory = false;
        }
      });
  }

  onWithdraw(): void {
    if (this.withdrawalForm.invalid) {
      return;
    }

    const withdrawalData = {
      elementId: this.data.element.id,
      quantity: this.withdrawalForm.value.quantity,
      purpose: this.withdrawalForm.value.purpose,
      comments: this.withdrawalForm.value.comments
    };

    this.isWithdrawing = true;
    this.withdrawalService.recordWithdrawal(withdrawalData)
      .subscribe({
        next: () => {
          this.snackBar.open('Entnahme erfolgreich registriert', 'OK', { duration: 3000 });

          // Update the element quantity
          this.data.element.quantity -= withdrawalData.quantity;

          // Update max validator for quantity field
          this.withdrawalForm.get('quantity')?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(this.data.element.quantity)
          ]);
          this.withdrawalForm.get('quantity')?.updateValueAndValidity();

          // Update withdrawal history
          this.loadWithdrawalHistory();

          // Reset form
          this.withdrawalForm.reset({
            quantity: 1,
            purpose: '',
            comments: ''
          });

          this.isWithdrawing = false;
        },
        error: (error) => {
          console.error('Error recording withdrawal:', error);
          this.snackBar.open('Fehler bei der Entnahme. Bitte versuchen Sie es erneut.', 'OK', { duration: 3000 });
          this.isWithdrawing = false;
        }
      });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
